// Products: Actions for getting product information to select within calculator.
import { Dispatch } from 'redux';

import { get, RequestExceptionWithResponse } from '@eika-infrastruktur/request';

import { AppState } from '../reducers';
import LoanProductURL from '../util/mappings/loanProductURL';
import { getRequestHeaders } from './tools';
import { actionTypes, AppActions } from './types';

type Action<T> = import('./types').Action<T>;
type FetchProductsResponse = import('../generated-types').MultipleCalculatorDataWebResponse;
type FetchProductInfoResponse = import('../generated-types').ObjectCalculatorDataWebResponse;

export interface UpdateProductAction extends Action<typeof actionTypes.UPDATE_PRODUCT> {
    type: typeof actionTypes.UPDATE_PRODUCT;
    payload: string;
}

export interface FetchProductsStartedAction extends Action<typeof actionTypes.FETCH_PRODUCTS_STARTED> {
    type: typeof actionTypes.FETCH_PRODUCTS_STARTED;
}

export interface FetchProductsFulfilledAction extends Action<typeof actionTypes.FETCH_PRODUCTS_FULFILLED> {
    type: typeof actionTypes.FETCH_PRODUCTS_FULFILLED;
    payload: FetchProductsResponse;
}

export interface FetchProductsFailedAction extends Action<typeof actionTypes.FETCH_PRODUCTS_FAILED> {
    type: typeof actionTypes.FETCH_PRODUCTS_FAILED;
    payload: RequestExceptionWithResponse;
}

export interface FetchProductInfoStartedAction extends Action<typeof actionTypes.FETCH_PRODUCT_INFO_STARTED> {
    type: typeof actionTypes.FETCH_PRODUCT_INFO_STARTED;
}

export interface FetchProductInfoFulfilledAction extends Action<typeof actionTypes.FETCH_PRODUCT_INFO_FULFILLED> {
    type: typeof actionTypes.FETCH_PRODUCT_INFO_FULFILLED;
    payload: FetchProductInfoResponse;
}

export interface FetchProductInfoFailedAction extends Action<typeof actionTypes.FETCH_PRODUCT_INFO_FAILED> {
    type: typeof actionTypes.FETCH_PRODUCT_INFO_FAILED;
    payload: RequestExceptionWithResponse;
}

export interface FetchProductInfoInvalidAction extends Action<typeof actionTypes.FETCH_PRODUCT_INFO_INVALID> {
    type: typeof actionTypes.FETCH_PRODUCT_INFO_INVALID;
}

export type ProductsActions =
    | UpdateProductAction
    | FetchProductsStartedAction
    | FetchProductsFulfilledAction
    | FetchProductsFailedAction
    | FetchProductInfoStartedAction
    | FetchProductInfoFulfilledAction
    | FetchProductInfoFailedAction
    | FetchProductInfoInvalidAction;

export const updateProduct = (vehicleURL: string): UpdateProductAction => ({
    type: actionTypes.UPDATE_PRODUCT,
    payload: vehicleURL,
});

export const fetchProductsStarted = (): FetchProductsStartedAction => ({
    type: actionTypes.FETCH_PRODUCTS_STARTED,
});
export const fetchProductsFulfilled = (json: FetchProductsResponse): FetchProductsFulfilledAction => ({
    type: actionTypes.FETCH_PRODUCTS_FULFILLED,
    payload: json,
});
export const fetchProductsFailed = (error: RequestExceptionWithResponse): FetchProductsFailedAction => ({
    type: actionTypes.FETCH_PRODUCTS_FAILED,
    payload: error,
});

export const fetchProductInfoStarted = (): FetchProductInfoStartedAction => ({
    type: actionTypes.FETCH_PRODUCT_INFO_STARTED,
});
export const fetchProductInfoFulfilled = (json: FetchProductInfoResponse): FetchProductInfoFulfilledAction => ({
    type: actionTypes.FETCH_PRODUCT_INFO_FULFILLED,
    payload: json,
});
export const fetchProductInfoFailed = (error: RequestExceptionWithResponse): FetchProductInfoFailedAction => ({
    type: actionTypes.FETCH_PRODUCT_INFO_FAILED,
    payload: error,
});
export const fetchProductInfoInvalid = (): FetchProductInfoInvalidAction => ({
    type: actionTypes.FETCH_PRODUCT_INFO_INVALID,
});

// Fetch All Products
export const fetchProducts =
    () =>
    async (dispatch: Dispatch<AppActions>, getState: () => AppState): Promise<void> => {
        const { loader, context } = getState();
        const { hasFetchedProducts, isFetchingProducts } = loader;
        if (!hasFetchedProducts && !isFetchingProducts) {
            try {
                dispatch(fetchProductsStarted());
                const response: FetchProductsResponse = await get(
                    `${actionTypes.RESOURCE_PATH}/calculator/all`,
                    getRequestHeaders(context.config.bankregnr),
                );
                dispatch(fetchProductsFulfilled(response));
            } catch (error) {
                dispatch(fetchProductsFailed(error));
            }
        }
    };

// Fetch Product Info by Loan Product
export const fetchProductInfo =
    (vehicleURL: string) =>
    async (dispatch: Dispatch<AppActions>, getState: () => AppState): Promise<void> => {
        const { loader, context } = getState();
        const { isFetchingProductInfo } = loader;
        if (!isFetchingProductInfo) {
            switch (vehicleURL) {
                case LoanProductURL.car:
                case LoanProductURL.carElectric:
                case LoanProductURL.carNoInsurance:
                case LoanProductURL.mc:
                case LoanProductURL.atv:
                case LoanProductURL.snowmobile:
                case LoanProductURL.campingCar:
                case LoanProductURL.campingCaravan:
                case LoanProductURL.boat: {
                    try {
                        dispatch(fetchProductInfoStarted());
                        const response: FetchProductInfoResponse = await get(
                            `${actionTypes.RESOURCE_PATH}/calculator/${vehicleURL}`,
                            getRequestHeaders(context.config.bankregnr),
                        );
                        dispatch(fetchProductInfoFulfilled(response));
                        dispatch(updateProduct(vehicleURL));
                    } catch (error) {
                        dispatch(fetchProductInfoFailed(error));
                    }
                    break;
                }
                default:
                    dispatch(fetchProductInfoInvalid());
            }
        }
    };
